import { useLogStore } from '@/stores/logStore';
import { Card } from '@/components/ui/card';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { PerformanceMetrics } from './PerformanceMetrics';

export function WorkflowStats() {
  const { logs } = useLogStore();

  // Group logs by date
  const logsByDate = logs.reduce(
    (acc, log) => {
      const date = new Date(log.timestamp).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = { date, success: 0, error: 0, info: 0, warning: 0 };
      }
      acc[date][log.level]++;
      return acc;
    },
    {} as Record<string, any>
  );

  const chartData = Object.values(logsByDate).reverse();

  // Stats by level
  const stats = {
    total: logs.length,
    success: logs.filter((l) => l.level === 'success').length,
    error: logs.filter((l) => l.level === 'error').length,
    warning: logs.filter((l) => l.level === 'warning').length,
    info: logs.filter((l) => l.level === 'info').length,
  };

  const successRate = stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0;

  return (
    <div className="space-y-8">
      {/* Overview Stats */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Execution Overview</h3>
          <p className="text-sm text-muted-foreground">All-time workflow execution statistics</p>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          <Card className="p-4 space-y-2 bg-green-500/5 border-green-500/20">
            <p className="text-sm font-medium text-muted-foreground">Successful</p>
            <p className="text-2xl font-bold text-green-600">{stats.success}</p>
          </Card>
          <Card className="p-4 space-y-2 bg-destructive/5 border-destructive/20">
            <p className="text-sm font-medium text-muted-foreground">Errors</p>
            <p className="text-2xl font-bold text-destructive">{stats.error}</p>
          </Card>
          <Card className="p-4 space-y-2 bg-amber-500/5 border-amber-500/20">
            <p className="text-sm font-medium text-muted-foreground">Warnings</p>
            <p className="text-2xl font-bold text-amber-600">{stats.warning}</p>
          </Card>
          <Card className="p-4 space-y-2 bg-blue-500/5 border-blue-500/20">
            <p className="text-sm font-medium text-muted-foreground">Info</p>
            <p className="text-2xl font-bold text-blue-600">{stats.info}</p>
          </Card>
          <Card className="p-4 space-y-2 bg-purple-500/5 border-purple-500/20">
            <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
            <p className="text-2xl font-bold text-purple-600">{successRate}%</p>
          </Card>
        </div>
      </div>

      {/* Trends */}
      {chartData.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold text-foreground mb-4">Execution Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="success" stackId="a" fill="#16a34a" name="Success" />
              <Bar dataKey="error" stackId="a" fill="#dc2626" name="Error" />
              <Bar dataKey="warning" stackId="a" fill="#d97706" name="Warning" />
              <Bar dataKey="info" stackId="a" fill="#2563eb" name="Info" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Performance Metrics */}
      <PerformanceMetrics />
      {logs.length === 0 && (
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">
            No execution history yet. Run a workflow to see statistics.
          </p>
        </Card>
      )}
    </div>
  );
}